import { useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { generateSEOMeta } from '@/components/feature/SEOMeta';
import { getArticleSchema, getBreadcrumbSchema, getWebPageSchema } from '@/components/feature/JsonLD';
import { comics, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const comic = useMemo(() => comics.find((c) => c.id === Number(id)), [id]);
  const categoryInfo = useMemo(() => (comic ? categories.find((c) => c.slug === comic.category) : null), [comic]);

  const related = useMemo(
    () => (comic ? comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4) : []),
    [comic]
  );

  const popular = useMemo(() => [...comics].sort((a, b) => b.views - a.views).slice(0, 4), []);

  const seo = useMemo(() => {
    if (!comic) return generateSEOMeta('article', { title: '', description: '', category: '' });
    return generateSEOMeta('article', {
      title: comic.title,
      description: comic.description,
      category: categoryInfo?.name || '',
    });
  }, [comic, categoryInfo]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    if (!comic) {
      navigate('/', { replace: true });
    }
  }, [comic, navigate]);

  useEffect(() => {
    document.title = seo.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', seo.description);
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', seo.keywords);
  }, [seo]);

  if (!comic) return null;

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return String(v);
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getArticleSchema(comic)) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getWebPageSchema(seo.title, seo.description, `https://www.hanmanzhijia.com/article/${comic.id}`)
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbSchema([
              { name: '首页', url: 'https://www.hanmanzhijia.com/' },
              { name: categoryInfo?.name || '分类', url: `https://www.hanmanzhijia.com/category/${comic.category}` },
              { name: comic.title },
            ])
          ),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryInfo?.name || '分类', href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="sticky top-24">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - 韩漫之家免费韩漫在线阅读`}
                      title={`${comic.title} - 韩漫之家高清下拉式全集`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      comic.status === '连载中'
                        ? 'bg-primary-50 text-primary-600 border border-primary-200'
                        : 'bg-secondary-100 text-secondary-700 border border-secondary-200'
                    }`}
                  >
                    {comic.status}
                  </span>
                  {categoryInfo && (
                    <Link
                      to={`/category/${categoryInfo.slug}`}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-transparent hover:border-primary-200 transition-all duration-200 whitespace-nowrap"
                    >
                      <i className={`${categoryInfo.icon} mr-1`}></i>
                      {categoryInfo.name}
                    </Link>
                  )}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900">
                  {comic.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    <strong className="text-foreground-700 font-medium">{comic.author}</strong>
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong className="text-foreground-700 font-medium">{comic.rating}</strong>
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {formatViews(comic.views)}次阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-book-open-line"></i>
                    共{comic.chapters.length}话
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    更新于 {comic.updateDate}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-secondary-50 text-secondary-700 border border-secondary-200/60"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6 p-5 rounded-xl bg-background-100 border border-background-200/60">
                  <h2 className="font-heading text-lg font-semibold text-foreground-800 mb-3">
                    作品简介
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="mt-6 flex gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all duration-200 text-sm whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-full transition-all duration-200 text-sm whitespace-nowrap cursor-pointer flex items-center gap-2 border border-background-200">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                </div>

                <div className="mt-8">
                  <h2 className="font-heading text-lg font-semibold text-foreground-800 mb-4">
                    章节列表
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {comic.chapters.map((ch) => (
                      <button
                        key={ch.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-200 text-left cursor-pointer"
                      >
                        <span className="text-sm font-medium text-foreground-700">
                          {ch.title}
                        </span>
                        <span className="text-xs text-foreground-400 flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-calendar-line text-[10px]"></i>
                          {ch.date}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {related.length > 0 && (
              <section className="mt-14">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  同类推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/article/${r.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                        <img
                          src={r.cover}
                          alt={`${r.title} - 同类韩漫推荐`}
                          title={`${r.title} - 韩漫之家免费阅读`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h3 className="mt-3 text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                        {r.title}
                      </h3>
                      <div className="mt-1 flex items-center gap-0.5">
                        <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                        <span className="text-xs text-accent-600 font-medium">{r.rating}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-14">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                热门韩漫
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {popular.map((p) => (
                  <Link
                    key={p.id}
                    to={`/article/${p.id}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={p.cover}
                        alt={`${p.title} - 热门韩漫推荐`}
                        title={`${p.title} - 韩漫之家`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h3 className="mt-3 text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                      {p.title}
                    </h3>
                    <div className="mt-1 flex items-center gap-0.5">
                      <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                      <span className="text-xs text-accent-600 font-medium">{p.rating}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </article>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}